import { Container, Graphics } from 'pixi.js';
import {
  PARTICLE_MAX_COUNT,
  PARTICLE_DEFAULT_LIFETIME,
  FOOD_PARTICLE_COUNT,
  FOOD_PARTICLE_SPEED_MIN,
  FOOD_PARTICLE_SPEED_MAX,
  FOOD_PARTICLE_SIZE_START,
  FOOD_PARTICLE_SIZE_END,
  FOOD_PARTICLE_LIFETIME,
  FOOD_PARTICLE_GRAVITY,
  POWERUP_PARTICLE_COUNT,
  POWERUP_PARTICLE_SPEED_MIN,
  POWERUP_PARTICLE_SPEED_MAX,
  POWERUP_PARTICLE_LIFETIME,
  DEATH_PARTICLE_COUNT,
  DEATH_SPARK_COUNT,
  DEATH_PARTICLE_SPEED_MIN,
  DEATH_PARTICLE_SPEED_MAX,
  DEATH_PARTICLE_SIZE_START,
  DEATH_PARTICLE_SIZE_END,
  DEATH_PARTICLE_LIFETIME,
  DEATH_SPARK_SPEED_MIN,
  DEATH_SPARK_SPEED_MAX,
  DEATH_SPARK_LIFETIME,
  EATING_BURST_PARTICLE_COUNT,
  EATING_BURST_SPEED_MIN,
  EATING_BURST_SPEED_MAX,
} from '../../constants';
import { EyeProtectionSettings, EyeProtectionLevel } from '../../ui/EyeProtectionToggle';

/**
 * Individual particle data
 */
interface Particle {
  /** Particle graphic */
  graphic: Graphics;
  /** Current X position */
  x: number;
  /** Current Y position */
  y: number;
  /** X velocity (pixels per second) */
  vx: number;
  /** Y velocity (pixels per second) */
  vy: number;
  /** Particle color */
  color: number;
  /** Starting size */
  startSize: number;
  /** Ending size */
  endSize: number;
  /** Remaining lifetime in seconds */
  lifetime: number;
  /** Total lifetime for progress calculation */
  maxLifetime: number;
  /** Gravity applied to particle (pixels per second squared) */
  gravity: number;
  /** Starting alpha */
  startAlpha: number;
  /** Whether particle is active */
  active: boolean;
}

/**
 * Particle spawn configuration
 */
export interface ParticleConfig {
  /** Spawn position X */
  x: number;
  /** Spawn position Y */
  y: number;
  /** Number of particles */
  count: number;
  /** Particle color */
  color: number;
  /** Minimum speed */
  speedMin: number;
  /** Maximum speed */
  speedMax: number;
  /** Starting size */
  sizeStart: number;
  /** Ending size */
  sizeEnd: number;
  /** Particle lifetime in seconds */
  lifetime: number;
  /** Gravity (0 = no gravity) */
  gravity?: number;
  /** Starting alpha */
  alpha?: number;
  /** Direction constraint (radians, undefined = 360 degrees) */
  direction?: number;
  /** Direction spread (radians, how much variance from direction) */
  spread?: number;
}

/**
 * ParticleSystem - Efficient particle effects manager
 *
 * Features:
 * - Object pooling for performance
 * - Configurable particle behaviors
 * - Multiple particle effects (food, powerup, death)
 * - Gravity and alpha fade support
 */
export class ParticleSystem {
  /** Main container for particles */
  public container: Container;

  /** Particle pool */
  private particles: Particle[] = [];

  /** Pool of inactive particles for reuse */
  private particlePool: Particle[] = [];

  constructor() {
    this.container = new Container();
    this.container.label = 'particle-system';

    // Pre-allocate particle pool
    this.initializePool();
  }

  /**
   * Initialize the particle object pool
   */
  private initializePool(): void {
    for (let i = 0; i < PARTICLE_MAX_COUNT; i++) {
      const graphic = new Graphics();
      graphic.visible = false;
      this.container.addChild(graphic);

      this.particlePool.push({
        graphic,
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        color: 0xffffff,
        startSize: 4,
        endSize: 1,
        lifetime: 0,
        maxLifetime: PARTICLE_DEFAULT_LIFETIME,
        gravity: 0,
        startAlpha: 1,
        active: false,
      });
    }
  }

  /**
   * Get a particle from the pool or create new if needed
   */
  private getParticle(): Particle | null {
    // Try to find an inactive particle
    for (const particle of this.particlePool) {
      if (!particle.active) {
        return particle;
      }
    }

    // Pool exhausted - find oldest active particle and recycle it
    let oldest: Particle | null = null;
    let oldestLifetime = Infinity;

    for (const particle of this.particles) {
      if (particle.lifetime < oldestLifetime) {
        oldestLifetime = particle.lifetime;
        oldest = particle;
      }
    }

    if (oldest) {
      oldest.active = false;
      oldest.graphic.visible = false;
      const index = this.particles.indexOf(oldest);
      if (index > -1) {
        this.particles.splice(index, 1);
      }
      return oldest;
    }

    return null;
  }

  /**
   * Get particle count reduction factor for eye protection mode
   * Returns 1.0 for off, 0.6 for mild (40% reduction), 0.3 for strong (70% reduction)
   */
  private getParticleCountFactor(): number {
    const level = EyeProtectionSettings.getInstance().getLevel();
    switch (level) {
      case EyeProtectionLevel.MILD:
        return 0.6; // 40% reduction
      case EyeProtectionLevel.STRONG:
        return 0.3; // 70% reduction
      default:
        return 1.0; // No reduction
    }
  }

  /**
   * Spawn particles with configuration
   */
  spawn(config: ParticleConfig): void {
    const {
      x,
      y,
      count,
      color,
      speedMin,
      speedMax,
      sizeStart,
      sizeEnd,
      lifetime,
      gravity = 0,
      alpha = 1,
      direction,
      spread = Math.PI * 2,
    } = config;

    // Reduce particle count when eye protection is enabled
    const effectiveCount = Math.max(1, Math.floor(count * this.getParticleCountFactor()));

    for (let i = 0; i < effectiveCount; i++) {
      const particle = this.getParticle();
      if (!particle) continue;

      // Calculate random direction
      let angle: number;
      if (direction !== undefined) {
        angle = direction + (Math.random() - 0.5) * spread;
      } else {
        angle = Math.random() * Math.PI * 2;
      }

      // Random speed within range
      const speed = speedMin + Math.random() * (speedMax - speedMin);

      // Initialize particle
      particle.x = x;
      particle.y = y;
      particle.vx = Math.cos(angle) * speed;
      particle.vy = Math.sin(angle) * speed;
      particle.color = color;
      particle.startSize = sizeStart;
      particle.endSize = sizeEnd;
      particle.lifetime = lifetime;
      particle.maxLifetime = lifetime;
      particle.gravity = gravity;
      particle.startAlpha = alpha;
      particle.active = true;
      particle.graphic.visible = true;

      // Draw initial particle
      this.drawParticle(particle);

      this.particles.push(particle);
    }
  }

  /**
   * Spawn food collection effect - enhanced for satisfying feedback
   */
  spawnFoodEffect(x: number, y: number, color: number): void {
    // Main color burst particles
    this.spawn({
      x,
      y,
      count: FOOD_PARTICLE_COUNT,
      color,
      speedMin: FOOD_PARTICLE_SPEED_MIN,
      speedMax: FOOD_PARTICLE_SPEED_MAX,
      sizeStart: FOOD_PARTICLE_SIZE_START,
      sizeEnd: FOOD_PARTICLE_SIZE_END,
      lifetime: FOOD_PARTICLE_LIFETIME,
      gravity: FOOD_PARTICLE_GRAVITY,
      alpha: 1,
    });

    // Skip additional effects for strong eye protection
    if (EyeProtectionSettings.getInstance().isStrong()) {
      return;
    }

    // Quick white flash burst - impact feel
    this.spawn({
      x,
      y,
      count: 6,
      color: 0xffffff,
      speedMin: EATING_BURST_SPEED_MIN,
      speedMax: EATING_BURST_SPEED_MAX,
      sizeStart: 8,
      sizeEnd: 0,
      lifetime: 0.15,
      gravity: 0,
      alpha: 0.8,
    });

    // Colorful secondary burst - joyful feel
    this.spawn({
      x,
      y,
      count: EATING_BURST_PARTICLE_COUNT,
      color: this.brightenColor(color),
      speedMin: EATING_BURST_SPEED_MIN * 0.8,
      speedMax: EATING_BURST_SPEED_MAX * 0.8,
      sizeStart: 5,
      sizeEnd: 1,
      lifetime: 0.35,
      gravity: 80,
      alpha: 0.9,
    });

    // Tiny sparkles floating upward
    this.spawn({
      x,
      y,
      count: 4,
      color: 0xffffff,
      speedMin: 30,
      speedMax: 60,
      sizeStart: 3,
      sizeEnd: 0,
      lifetime: 0.4,
      gravity: -60, // Float up
      alpha: 0.7,
    });
  }

  /**
   * Brighten a color for highlight effects
   */
  private brightenColor(color: number): number {
    const r = Math.min(255, ((color >> 16) & 0xff) + 50);
    const g = Math.min(255, ((color >> 8) & 0xff) + 50);
    const b = Math.min(255, (color & 0xff) + 50);
    return (r << 16) | (g << 8) | b;
  }

  /**
   * Spawn powerup collection effect
   */
  spawnPowerupEffect(x: number, y: number, color: number): void {
    this.spawn({
      x,
      y,
      count: POWERUP_PARTICLE_COUNT,
      color,
      speedMin: POWERUP_PARTICLE_SPEED_MIN,
      speedMax: POWERUP_PARTICLE_SPEED_MAX,
      sizeStart: 6,
      sizeEnd: 2,
      lifetime: POWERUP_PARTICLE_LIFETIME,
      gravity: 0,
      alpha: 1,
    });

    // Add secondary sparkle effect
    this.spawn({
      x,
      y,
      count: 8,
      color: 0xffffff,
      speedMin: 60,
      speedMax: 120,
      sizeStart: 3,
      sizeEnd: 0,
      lifetime: 0.3,
      gravity: -50, // Float upward
      alpha: 0.8,
    });
  }

  /**
   * Spawn death explosion effect - big, dramatic explosion
   * @param x Center X position
   * @param y Center Y position
   * @param color Primary worm color
   * @param segmentPositions Optional array of segment positions for chain explosion
   */
  spawnDeathExplosion(
    x: number,
    y: number,
    color: number,
    segmentPositions?: { x: number; y: number }[]
  ): void {
    // Main explosion burst - large colorful particles
    this.spawn({
      x,
      y,
      count: DEATH_PARTICLE_COUNT,
      color,
      speedMin: DEATH_PARTICLE_SPEED_MIN,
      speedMax: DEATH_PARTICLE_SPEED_MAX,
      sizeStart: DEATH_PARTICLE_SIZE_START,
      sizeEnd: DEATH_PARTICLE_SIZE_END,
      lifetime: DEATH_PARTICLE_LIFETIME,
      gravity: 100,
      alpha: 1,
    });

    // White/yellow hot core sparks - fast and bright
    this.spawn({
      x,
      y,
      count: DEATH_SPARK_COUNT,
      color: 0xffffaa,
      speedMin: DEATH_SPARK_SPEED_MIN,
      speedMax: DEATH_SPARK_SPEED_MAX,
      sizeStart: 6,
      sizeEnd: 1,
      lifetime: DEATH_SPARK_LIFETIME,
      gravity: 0,
      alpha: 1,
    });

    // Red/orange fire particles (skip only for strong eye protection)
    if (!EyeProtectionSettings.getInstance().isStrong()) {
      this.spawn({
        x,
        y,
        count: 12,
        color: 0xff6633,
        speedMin: 100,
        speedMax: 250,
        sizeStart: 8,
        sizeEnd: 2,
        lifetime: 0.6,
        gravity: -50, // Rise like flames
        alpha: 0.9,
      });

      // Smoke particles - slower, darker, longer lasting
      this.spawn({
        x,
        y,
        count: 8,
        color: 0x444444,
        speedMin: 30,
        speedMax: 80,
        sizeStart: 15,
        sizeEnd: 25,
        lifetime: 1.2,
        gravity: -30, // Rise slowly
        alpha: 0.4,
      });
    }

    // If we have segment positions, spawn secondary explosions along the body
    // Skip for strong eye protection (too much visual noise)
    if (segmentPositions && segmentPositions.length > 0 && !EyeProtectionSettings.getInstance().isStrong()) {
      // Spawn smaller explosions at intervals along the body
      const step = Math.max(1, Math.floor(segmentPositions.length / 5));
      for (let i = step; i < segmentPositions.length; i += step) {
        const seg = segmentPositions[i];
        if (!seg) continue;

        // Delay effect slightly based on distance from head
        const delayFactor = i / segmentPositions.length;

        // Smaller secondary explosions
        this.spawn({
          x: seg.x,
          y: seg.y,
          count: 6,
          color,
          speedMin: 80,
          speedMax: 180,
          sizeStart: 6,
          sizeEnd: 1,
          lifetime: DEATH_PARTICLE_LIFETIME * (0.6 + delayFactor * 0.4),
          gravity: 80,
          alpha: 0.8,
        });

        // Small sparks at each segment
        this.spawn({
          x: seg.x,
          y: seg.y,
          count: 4,
          color: 0xffff88,
          speedMin: 100,
          speedMax: 200,
          sizeStart: 3,
          sizeEnd: 0,
          lifetime: 0.3,
          gravity: 0,
          alpha: 1,
        });
      }
    }
  }

  /**
   * Spawn kill effect - visual feedback when you kill someone
   * @param x Position X
   * @param y Position Y
   * @param victimColor Color of the killed worm
   */
  spawnKillEffect(x: number, y: number, victimColor: number): void {
    // Bright white flash at center - arcade impact feel
    this.spawn({
      x,
      y,
      count: 10,
      color: 0xffffff,
      speedMin: 20,
      speedMax: 60,
      sizeStart: 20,
      sizeEnd: 0,
      lifetime: 0.15,
      gravity: 0,
      alpha: 1,
    });

    // Gold star burst to celebrate the kill
    this.spawn({
      x,
      y,
      count: 20,
      color: 0xffd700,
      speedMin: 120,
      speedMax: 250,
      sizeStart: 10,
      sizeEnd: 2,
      lifetime: 0.7,
      gravity: 0,
      alpha: 1,
    });

    // Rainbow burst - retro arcade celebration (skip for strong eye protection)
    if (!EyeProtectionSettings.getInstance().isStrong()) {
      const rainbowColors = [0xff0000, 0xff8800, 0xffff00, 0x00ff00, 0x00ffff, 0xff00ff];
      for (const rainbowColor of rainbowColors) {
        this.spawn({
          x,
          y,
          count: 3,
          color: rainbowColor,
          speedMin: 150,
          speedMax: 280,
          sizeStart: 6,
          sizeEnd: 1,
          lifetime: 0.5,
          gravity: 50,
          alpha: 1,
        });
      }
    }

    // Victim color particles flying outward
    this.spawn({
      x,
      y,
      count: 16,
      color: victimColor,
      speedMin: 180,
      speedMax: 350,
      sizeStart: 8,
      sizeEnd: 1,
      lifetime: 0.6,
      gravity: 100,
      alpha: 0.9,
    });
  }

  /**
   * Draw a particle
   */
  private drawParticle(particle: Particle): void {
    const progress = 1 - particle.lifetime / particle.maxLifetime;
    const size = particle.startSize + (particle.endSize - particle.startSize) * progress;
    const alpha = particle.startAlpha * (1 - progress);

    const g = particle.graphic;
    g.clear();
    g.circle(0, 0, size);
    g.fill({ color: particle.color, alpha });
    g.position.set(particle.x, particle.y);
  }

  /**
   * Update all particles
   * @param dt Delta time in seconds
   */
  update(dt: number): void {
    const toRemove: number[] = [];

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particle = this.particles[i];
      if (!particle) continue;

      // Update lifetime
      particle.lifetime -= dt;

      if (particle.lifetime <= 0) {
        // Deactivate particle
        particle.active = false;
        particle.graphic.visible = false;
        toRemove.push(i);
        continue;
      }

      // Apply gravity
      particle.vy += particle.gravity * dt;

      // Update position
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;

      // Redraw particle
      this.drawParticle(particle);
    }

    // Remove dead particles from active list
    for (const index of toRemove) {
      this.particles.splice(index, 1);
    }
  }

  /**
   * Get active particle count
   */
  getActiveCount(): number {
    return this.particles.length;
  }

  /**
   * Clear all particles
   */
  clear(): void {
    for (const particle of this.particles) {
      particle.active = false;
      particle.graphic.visible = false;
    }
    this.particles.length = 0;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.clear();
    for (const particle of this.particlePool) {
      particle.graphic.destroy();
    }
    this.particlePool.length = 0;
    this.container.destroy({ children: true });
  }
}
